// frontend2/src/pages/HoFunds/HoFunds.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet,
  faArrowUp,
  faArrowDown,
  faMinusCircle,
  faRefresh,
  faBuilding,
  faReceipt,
  faCalendarAlt,
  faFilter,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { maskVpa } from '../../utils/maskVpa';
// [CASH-MODE] UPI / Cash filter + indication (withdraw rows have no mode)
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, rowPaymentMode, totalsByModeFromRows } from '../../utils/paymentMode';

const HoFunds = () => {
  const [hoData, setHoData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const { can } = usePermissions();
  const canWithdraw = can('hoFunds.overview.withdraw');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Date and filter states
  const todayStr = new Date().toISOString().split('T')[0];
  const thirtyDaysAgoStr = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [transactionType, setTransactionType] = useState('all');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [paginationInfo, setPaginationInfo] = useState({ totalRecords: 0 });

  // Withdrawal dialog state
  const [openWithdrawModal, setOpenWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [withdrawError, setWithdrawError] = useState('');

  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, [token]);

  const fetchHoFundsData = useCallback(async () => {
    if (!token) {
      setError('Authentication token missing. Please login again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        startDate,
        endDate,
        page: page + 1,
        limit: rowsPerPage,
        transactionType: transactionType === 'all' ? undefined : transactionType,
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };

      const response = await axios.post(
        `${API_BASE_URL}/hoFunds/data`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200 && response.data && response.data.status === 'SUCCESS') {
        const data = response.data.data;
        setHoData(data);
        const txList = (data && data.transactions && data.transactions.all) ? data.transactions.all : [];
        setTransactions(txList);
        setPaginationInfo((data && data.pagination) ? data.pagination : { totalRecords: 0 });
      } else {
        setError((response.data && response.data.message) || 'Failed to fetch HO funds data.');
      }
    } catch (err) {
      console.error('Error fetching HO funds data:', err);
      if (err.response && err.response.status === 403) {
        setError('Access denied. You do not have permission to view HO Funds.');
      } else {
        const errMsg = (err.response && err.response.data && err.response.data.message) || 'Error connecting to server.';
        setError(errMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate, transactionType, page, rowsPerPage, paymentMode]); // [CASH-MODE] paymentMode

  useEffect(() => {
    fetchHoFundsData();
  }, [fetchHoFundsData]);

  const handleOpenWithdrawModal = () => {
    if (!canWithdraw) return;
    setWithdrawAmount('');
    setWithdrawReason('');
    setAdminNotes('');
    setWithdrawError('');
    setOpenWithdrawModal(true);
  };

  const handleCloseWithdrawModal = () => {
    setOpenWithdrawModal(false);
  };

  const handleProcessWithdrawal = async (e) => {
    e.preventDefault();
    setWithdrawError('');
    if (!canWithdraw) {
      setWithdrawError('You do not have permission to reduce the HO balance.');
      return;
    }

    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) {
      setWithdrawError('Please enter a valid positive withdrawal amount.');
      return;
    }

    const currentBal = (hoData && hoData.hoInfo && hoData.hoInfo.totalBalance) ? hoData.hoInfo.totalBalance : 0;
    if (amt > currentBal) {
      setWithdrawError(`Amount cannot exceed available HO balance (${formatCurrency(currentBal)}).`);
      return;
    }

    if (!withdrawReason || withdrawReason.trim().length === 0) {
      setWithdrawError('Withdrawal reason is required.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/hoFunds/withdraw`,
        {
          amount: amt,
          reason: withdrawReason.trim(),
          adminNotes: adminNotes.trim() || undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200 && response.data && response.data.status === 'SUCCESS') {
        const newBal = (response.data.data && response.data.data.balanceInfo && response.data.data.balanceInfo.newBalance) || 0;
        setSuccessMessage(`Successfully reduced HO Balance by ${formatCurrency(amt)}! New Balance: ${formatCurrency(newBal)}`);
        handleCloseWithdrawModal();
        fetchHoFundsData();

        setTimeout(() => {
          setSuccessMessage('');
        }, 6000);
      } else {
        setWithdrawError((response.data && response.data.message) || 'Failed to process withdrawal.');
      }
    } catch (err) {
      console.error('Error processing HO withdrawal:', err);
      const errMsg = (err.response && err.response.data && err.response.data.message) || 'Server error during withdrawal process.';
      setWithdrawError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getUserLevelText = (lvl) => {
    switch (lvl) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      case 4: return 'MSB';
      case 5: return 'MSB Staff';
      default: return 'Admin';
    }
  };

  const hoTotalBalance = (hoData && hoData.hoInfo && hoData.hoInfo.totalBalance) || 0;
  const hoLastUpdated = (hoData && hoData.hoInfo && hoData.hoInfo.lastUpdated) ? new Date(hoData.hoInfo.lastUpdated).toLocaleString() : 'N/A';
  const totalAddAmount = (hoData && hoData.summary && hoData.summary.dateRange && hoData.summary.dateRange.totalAdd) || 0;
  const addCount = (hoData && hoData.summary && hoData.summary.dateRange && hoData.summary.dateRange.addCount) || 0;
  const totalWithdrawnAmount = (hoData && hoData.hoInfo && hoData.hoInfo.totalWithdrawn !== undefined) ? hoData.hoInfo.totalWithdrawn : ((hoData && hoData.summary && hoData.summary.dateRange && hoData.summary.dateRange.totalWithdraw) || 0);
  // [CASH-MODE] with a mode selected only that mode's credit rows are shown (withdrawals have no mode)
  const visibleTransactions = paymentModeParam(paymentMode)
    ? transactions.filter((t) => t.method === 'add' && rowPaymentMode(t) === paymentMode)
    : transactions;
  const apiTotalsByMode = (hoData && ((hoData.summary && hoData.summary.dateRange && hoData.summary.dateRange.totalsByMode) || (hoData.summary && hoData.summary.totalsByMode) || hoData.totalsByMode)) || null;
  const modeTotals = apiTotalsByMode || totalsByModeFromRows(visibleTransactions.filter((t) => t.method === 'add'), (t) => t.amount);
  const modeAmountKey = modeTotals?.UPI?.totalAdd !== undefined ? 'totalAdd' : 'amount';
  const withdrawalCount = (hoData && hoData.hoInfo && hoData.hoInfo.withdrawalCount !== undefined) ? hoData.hoInfo.withdrawalCount : 0;

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br /><br />

        {/* Header Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FontAwesomeIcon icon={faBuilding} style={{ color: '#0284c7' }} />
            Company Income
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton color="primary" onClick={fetchHoFundsData} title="Refresh Data">
              <FontAwesomeIcon icon={faRefresh} />
            </IconButton>
            <Chip 
              label={`Logged in: ${getUserLevelText(userLevel)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {successMessage}
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1">{error}</Typography>
          </Alert>
        )}

        {/* Grand Total HO Balance Featured Card */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card 
              elevation={4} 
              sx={{ 
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
                color: '#ffffff',
                borderRadius: 3,
                p: 1
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 1.2, fontWeight: 700 }}>
                      Head Office Holding Amount
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, color: '#38bdf8' }}>
                      {formatCurrency(hoTotalBalance)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 1, display: 'block' }}>
                      Grand Total Accumulated HO Share Balance
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(56, 189, 248, 0.15)', p: 1.5, borderRadius: 2 }}>
                    <FontAwesomeIcon icon={faWallet} size="2x" style={{ color: '#38bdf8' }} />
                  </Box>
                </Box>

                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Last Updated: {hoLastUpdated}
                  </Typography>

                  {canWithdraw && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="medium"
                      startIcon={<FontAwesomeIcon icon={faMinusCircle} />}
                      onClick={handleOpenWithdrawModal}
                      sx={{ 
                        fontWeight: 700,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
                      }}
                    >
                      Reduce HO Balance (Withdraw)
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Metrics */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      <FontAwesomeIcon icon={faArrowUp} style={{ color: '#16a34a', marginRight: '6px' }} />
                      Total Income Credited
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#16a34a', mt: 1 }}>
                      {formatCurrency(totalAddAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({addCount} Splitup Credits)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      <FontAwesomeIcon icon={faArrowDown} style={{ color: '#dc2626', marginRight: '6px' }} />
                      Total Reductions / Withdrawn
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626', mt: 1 }}>
                      {formatCurrency(totalWithdrawnAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({withdrawalCount} Manual Reductions)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Filter Section */}
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <FontAwesomeIcon icon={faFilter} style={{ color: '#0284c7' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Filter HO Transactions
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={transactionType}
                  label="Transaction Type"
                  onChange={(e) => setTransactionType(e.target.value)}
                >
                  <MenuItem value="all">All Transactions</MenuItem>
                  <MenuItem value="add">Credits (Splitups)</MenuItem>
                  <MenuItem value="withdraw">Debits (Reductions)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* [CASH-MODE] */}
            <Grid item xs={12} sm={3}>
              <PaymentModeFilter value={paymentMode} onChange={(v) => { setPaymentMode(v); setPage(0); }} fullWidth />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => { setPage(0); fetchHoFundsData(); }}
                startIcon={<FontAwesomeIcon icon={faCalendarAlt} />}
                sx={{ borderRadius: 2 }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* [CASH-MODE] */}
        <PaymentModeTotals
          totals={modeTotals}
          amountKey={modeAmountKey}
          title={apiTotalsByMode ? 'HO Income' : 'HO Income (this page)'}
          sx={{ mb: 2 }}
        />

        {/* Transactions Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress color="primary" size={50} />
            <Typography variant="body1" sx={{ ml: 2 }}>Loading HO Fund transactions...</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
            <Table aria-label="HO Fund Transactions Table">
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Sl.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>{/* [CASH-MODE] */}
                  <TableCell sx={{ fontWeight: 700 }}>HO Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Bill Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Shop / VPA Details</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason / Notes</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Processed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTransactions.map((txn, index) => ( // [CASH-MODE] filtered list
                  <TableRow 
                    key={txn.id}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f1f5f9' },
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                        {txn.serialNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {new Date(txn.transactionDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {txn.method === 'add' ? (
                        <Chip
                          icon={<FontAwesomeIcon icon={faArrowUp} />}
                          label="CREDIT (Splitup)"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          icon={<FontAwesomeIcon icon={faArrowDown} />}
                          label="DEBIT (Reduction)"
                          color="error"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </TableCell>
                    {/* [CASH-MODE] withdraw rows have no mode */}
                    <TableCell>
                      {txn.method === 'withdraw' ? '-' : <PaymentModeChip row={txn} />}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 700, 
                          color: txn.method === 'add' ? '#16a34a' : '#dc2626' 
                        }}
                      >
                        {txn.method === 'add' ? `+ ${formatCurrency(txn.amount)}` : `- ${formatCurrency(txn.amount)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {txn.originalTransaction ? formatCurrency(txn.originalTransaction.totalAmount) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {txn.shop ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {txn.shop.businessName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Seller: {txn.shop.sellerName} | {txn.shop.city}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {maskVpa(txn.vpaId, userLevel) || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {txn.reason || (txn.originalTransaction ? `Partner Ref: ${txn.originalTransaction.partnerRefNumber || 'Auto'}` : 'System Record')}
                      </Typography>
                      {txn.adminNotes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Note: {txn.adminNotes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {txn.withdrawnByName || (txn.method === 'add' ? 'System (Auto Splitup)' : 'Super Admin')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {visibleTransactions.length === 0 && ( // [CASH-MODE]
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <FontAwesomeIcon icon={faReceipt} size="3x" style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                      <Typography variant="h6" color="text.secondary">
                        No HO Fund transactions found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Try adjusting date filters or transaction type.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={paginationInfo.totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* Withdrawal Modal Dialog */}
        <Dialog 
          open={openWithdrawModal} 
          onClose={handleCloseWithdrawModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ 
            sx: { 
              borderRadius: 4, 
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)' 
            } 
          }}
        >
          {/* Header */}
          <DialogTitle 
            sx={{ 
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
              color: '#ffffff',
              py: 2.5,
              px: 3,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box 
                sx={{ 
                  backgroundColor: 'rgba(245, 158, 11, 0.2)', 
                  color: '#f59e0b',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faMinusCircle} size="lg" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#ffffff' }}>
                  Reduce HO Funds Balance
                </Typography>

              </Box>
            </Box>
            <IconButton 
              onClick={handleCloseWithdrawModal}
              sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </DialogTitle>

          <form onSubmit={handleProcessWithdrawal}>
            <DialogContent sx={{ p: 3 }}>
              {withdrawError && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{withdrawError}</Typography>
                </Alert>
              )}

              {/* Balance Banner */}
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 2.5, 
                  backgroundColor: '#f0f9ff', 
                  borderRadius: 3, 
                  border: '1px solid #bae6fd',
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Current Head Office Balance
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#0284c7', mt: 0.5 }}>
                    {formatCurrency(hoTotalBalance)}
                  </Typography>
                </Box>
                <Box sx={{ backgroundColor: 'rgba(2, 132, 199, 0.12)', p: 1.5, borderRadius: 2 }}>
                  <FontAwesomeIcon icon={faBuilding} size="lg" style={{ color: '#0284c7' }} />
                </Box>
              </Box>

              <TextField
                label="Reduction Amount (₹)"
                type="number"
                fullWidth
                required
                margin="normal"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount to reduce/withdraw"
                inputProps={{ step: '0.01', min: '0.01' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5
                  }
                }}
              />

              <TextField
                label="Reason for Reduction / Withdrawal"
                fullWidth
                required
                margin="normal"
                multiline
                rows={2}
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                placeholder="e.g. Head Office Operational Expense / Software Payout"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5
                  }
                }}
              />

              <TextField
                label="Admin Notes (Optional)"
                fullWidth
                margin="normal"
                multiline
                rows={2}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Internal audit reference or approval notes"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5
                  }
                }}
              />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button 
                onClick={handleCloseWithdrawModal} 
                disabled={submitting}
                sx={{ 
                  color: '#64748b', 
                  fontWeight: 600, 
                  textTransform: 'none',
                  px: 2.5,
                  borderRadius: 2 
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <FontAwesomeIcon icon={faMinusCircle} />}
                sx={{ 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#ffffff',
                  fontWeight: 700, 
                  textTransform: 'none',
                  px: 3,
                  py: 1.2,
                  borderRadius: 2.5,
                  boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  }
                }}
              >
                {submitting ? 'Processing...' : 'Confirm Reduction'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

      </Box>
    </div>
  );
};

export default HoFunds;
